/**
 * The field-tracking wire layer — v17 F4, the web surfaces over F1/F2/F3.
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVICES, NOT GUESSED
 * ===========================================================================
 * Every shape below is copied field-for-field from the API modules that serve
 * it: `AttendanceRowDto` from `apps/api/src/field/attendance/attendance.dto.ts`,
 * `FieldVisitDto` from `field/visits/visit-dto.ts`, `LiveSessionDto` and
 * `FieldConfigDto` from `field/location/field-location.dto.ts`, and
 * `FieldTimelineDto` from `field/timeline/field-timeline.service.ts`. Dates are
 * `Date` on the server and STRINGS on the wire; DECIMALs (`lat`, `lng`,
 * `arrival_distance_m`) are STRINGS end to end (the house rule
 * `entities/transformers.ts` records) and are never `Number()`-ed here except
 * inside the map projection, which owns its own parse.
 *
 * ===========================================================================
 * A POSITION FIELD CAN COME BACK AS A REDACTION MARKER, NOT COORDINATES
 * ===========================================================================
 * Rule 14: location is personal data. The API's choke points
 * (`position-masking.ts`, `position-view.ts`, `position-visibility.ts`)
 * collapse another person's position to `'•••'` for a caller without
 * `field.location.view` — the same marker money wears, and like money it is
 * the WHOLE object: a masked position exposes neither accuracy nor capture
 * time. `null` is a DIFFERENT fact — never captured, or purged under the
 * per-country retention window — and the two must not render alike.
 * `classifyPosition` in `position.ts` is the one place that distinction is
 * made; every screen asks it, none re-derives it.
 *
 * THE CLIENT BRANCHES ON THE VALUE, NEVER ON ITS OWN PERMISSION GUESS. The
 * server has already decided what this caller may see, per row, per subject
 * (self is raw even without the key). A `can()` check that second-guessed the
 * payload would be a second implementation of the Rule-14 decision — wrong the
 * day the matrix changes, and wrong TODAY for self rows.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';

/* ────────────────────────────────────────────────────────────── positions ── */

/**
 * The redaction marker, byte-identical to the money one. Transcribed from
 * `MASKED_POSITION` in `apps/api/src/field/attendance/position-masking.ts`;
 * `field-registers.test.tsx` asserts the spelling against that source so a
 * change there breaks a test here rather than rendering the marker as text.
 */
export const MASKED_POSITION = '•••';

/** The raw view — what the subject themself, or a `field.location.view` holder, receives. */
export interface PositionView {
  /** Device-claimed latitude, DECIMAL(9,6) string as stored. */
  lat: string;
  /** Device-claimed longitude, DECIMAL(10,6) string as stored. */
  lng: string;
  /** Device-reported accuracy radius in meters, or null. Never a gate. */
  accuracy_m: string | null;
  /** DEVICE clock at capture — a claim, worded as one ("device reported"). */
  captured_at: string;
  /** SERVER receipt — authoritative. */
  recorded_at: string;
}

/** What any position field renders as: raw, the mask, or absent. */
export type MaskedPosition = PositionView | typeof MASKED_POSITION | null;

/* ───────────────────────────────────────────────────────────── attendance ── */

/** One attendance row — `attendance.dto.ts` (`AttendanceRowDto`). */
export interface AttendanceRow {
  id: string;
  country_id: number;
  user_id: string;
  user_name: string | null;
  /** Country-local calendar day of the clock-in ('YYYY-MM-DD') — computed SERVER-side. */
  work_date: string;
  /** SERVER time, ISO-8601 UTC. */
  clock_in_at: string;
  /** SERVER time; null while the cycle is open. */
  clock_out_at: string | null;
  clock_in_position: MaskedPosition;
  clock_out_position: MaskedPosition;
  /**
   * Open AND its work_date is already past in the country's own timezone — the
   * honest render of a forgotten clock-out. The server never auto-closes
   * (fabricating a closing time is the anti-spec); this flag is how the
   * register says so without inventing one either.
   */
  missing_clock_out: boolean;
}

/* ───────────────────────────────────────────────────────────────── visits ── */

/**
 * `field_visits.purpose` — the closed vocabulary migration 52 declares
 * (`FIELD_VISIT_PURPOSES`, entity order). 'other' is first THERE for a MySQL
 * enum-default reason; the filter chips reorder for the reader (see
 * `VISIT_PURPOSE_FILTERS`) because a chip row is not an INSERT statement.
 * `field-registers.test.tsx` asserts this list against the entity source.
 */
export const VISIT_PURPOSES = [
  'other',
  'support',
  'sales',
  'installation',
  'training',
  'collection',
] as const;
export type VisitPurpose = (typeof VISIT_PURPOSES)[number];

/**
 * Arrival agreement (`visit-dto.ts`): recorded, never enforced — AND MASKED FOR
 * MOST SEATS SINCE v17 RULING 6.
 *
 * The tri-state is still honest ('undeterminable' is not 'disagrees'), but a
 * fourth arm arrived: `'•••'`. It is here for the same reason the metres wear
 * the mask, one step worse. `arrival_agrees` is `distance ≤ the country's
 * `arrival_agree_radius_m``, and that radius is a `config.manage` PATCH away, so
 * a seat that can move it and re-read the flag is holding a comparator it can
 * AIM: the API suite measured 16 radius probes narrowing one visit's ring from
 * the band's 4750 m to 1 m, and three of those recovering the engineer's
 * position to 0.51 m. So the flag now answers to `field.location.view` (or
 * self), exactly as the stamps and the metres do.
 *
 * WHAT THE SCREENS MUST NOT DO WITH IT: fall through to the 'undeterminable'
 * copy. "No position comparison — one side had no coordinates" is FALSE for a
 * masked seat; a comparison exists and is withheld. `classifyAgreement` in
 * `position.ts` is the one place these four are told apart.
 */
export type ArrivalAgreement =
  | 'agrees'
  | 'disagrees'
  | 'undeterminable'
  | typeof MASKED_POSITION;

/**
 * The coarse arrival band — `position-view.ts` (`ArrivalDistanceBand`), v17 F-1.
 * Boundaries are CLOSED ON THE UPPER SIDE, and the names say so: `within_250m`
 * is "≤ 250 m", `within_5km` is "> 250 m and ≤ 5 km".
 */
export type ArrivalDistanceBand = 'within_250m' | 'within_5km' | 'beyond_5km';

/**
 * The arrival distance as it arrives: metres · `'•••'` · `null` — the same
 * three states a position has, for the same reason and by the same server
 * decision. `classifyDistance` in `position.ts` is the one place they are told
 * apart.
 */
export type MaskedDistance = string | typeof MASKED_POSITION | null;

/** One visit — `visit-dto.ts` (`FieldVisitDto`). */
export interface VisitRow {
  id: string;
  country_id: number;
  /** The engineer — the visit's data subject for Rule-14 purposes. */
  user_id: string;
  user_name: string | null;
  party_id: string;
  party_name: string | null;
  purpose: VisitPurpose;
  arrive_at: string;
  leave_at: string | null;
  /** `leave_at === null`, precomputed server-side. */
  open: boolean;
  arrive_position: MaskedPosition;
  leave_position: MaskedPosition;
  /**
   * Server-computed Haversine (arrive stamp ↔ party premises), metres,
   * DECIMAL(8,1) string — AND IT WEARS THE MASK (v17 F-1).
   *
   * It used to ship unmasked on the F0 D4 reasoning that a distance is a ring
   * rather than a coordinate. Three rings are a coordinate: a Coordinator holds
   * `field.visits.view` AND `parties.create`, so it places its own anchors, and
   * the API suite measured three of them recovering an engineer's stored
   * position to 0.0076 m. So the METRES now answer to `field.location.view`
   * (or self) exactly as the stamps do, and every seat gets
   * {@link FieldVisitRow.arrival_distance_band} instead — which is why the
   * register can still say "over 5 km" beside a position cell reading '•••'.
   */
  arrival_distance_m: MaskedDistance;
  /**
   * The coarse band EVERY register seat gets, computed server-side. `null` =
   * no distance was computable (no premises, or no arrive stamp) — the same
   * "absent, not secret" rule the positions follow. NEVER re-derive this from
   * `arrival_distance_m`: that field is the mask on most seats, and a client
   * that computed its own band would be a second copy of the server's F-1
   * decision.
   */
  arrival_distance_band: ArrivalDistanceBand | null;
  /**
   * The raw flag — masked on the same seats as the metres (v17 ruling 6).
   * Screens render {@link VisitRow.arrival_agreement} instead; this is here
   * because the wire carries it and a client that dropped it from the type
   * would be free to invent its own tri-state from `arrival_distance_band`.
   */
  arrival_agrees: boolean | typeof MASKED_POSITION | null;
  arrival_agreement: ArrivalAgreement;
  outcome_notes: string | null;
  created_at: string;
  updated_at: string;
}

/* ─────────────────────────────────────────────────────────────── sessions ── */

export type SessionStatus =
  | 'requested'
  | 'declined'
  | 'expired_unanswered'
  | 'active'
  | 'ended';

/** One live-session consent record — `field-location.dto.ts` (`LiveSessionDto`). */
export interface LiveSessionRow {
  id: string;
  country_id: number;
  engineer_user_id: string;
  engineer_name: string | null;
  /** null = self-started; set = the manager whose request this consent answers. */
  requested_by_user_id: string | null;
  requested_by_name: string | null;
  status: string;
  duration_minutes: number;
  requested_at: string | null;
  accepted_at: string | null;
  started_at: string | null;
  ends_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  /** SERVER truth for "tracking is on" — never recomputed from a client clock. */
  tracking_active: boolean;
  seconds_remaining: number | null;
  created_at: string;
}

/* ─────────────────────────────────────────────────────────────── timeline ── */

/**
 * `GET /countries/:cc/field/timeline?user_id&date` — the composed day
 * (`field-timeline.service.ts`). Every position inside rode its own stream's
 * choke point; this endpoint adds no coordinate channel of its own.
 */
export interface FieldTimeline {
  user_id: string;
  /** The country-local calendar day this composes (`countries.tz`). */
  date: string;
  country: string;
  attendance: AttendanceRow | null;
  /** Visits whose ARRIVE fell on the day, oldest first. */
  visits: VisitRow[];
  /** Present only when `sessions_visible`. */
  sessions: LiveSessionRow[];
  /**
   * FALSE when the caller may not read this engineer's session records (B4's
   * list rule: self or `field.location.view`) — stated, never silently empty.
   * The page renders the statement; an empty array with `true` is a day with
   * no sessions, a DIFFERENT fact.
   */
  sessions_visible: boolean;
}

/* ───────────────────────────────────────────────────────────────── config ── */

/** Per-country field config — `field-location.dto.ts` (`FieldConfigDto`). */
export interface FieldConfig {
  country_id: number;
  attendance_enabled: boolean;
  visits_enabled: boolean;
  live_sessions_enabled: boolean;
  arrival_agree_radius_m: number;
  live_max_minutes: number;
  retention_days_stamps: number;
  retention_days_trail: number;
  updated_at: string;
}

/**
 * The PATCH body — every field optional, and the screen sends a DIFF, never a
 * snapshot (the CountrySettings rule: a save must not drag an untouched value
 * into a request that was only meant to change one number).
 */
export interface FieldConfigPatch {
  attendance_enabled?: boolean;
  visits_enabled?: boolean;
  live_sessions_enabled?: boolean;
  arrival_agree_radius_m?: number;
  live_max_minutes?: number;
  retention_days_stamps?: number;
  retention_days_trail?: number;
}

/* ──────────────────────────────────────────────────────────────── envelope ── */

export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  total: number;
}

/* ─────────────────────────────────────────────────────────────────── reads ── */

export interface AttendanceFilters {
  /** Engineer id, passed through as the URL holds it — the server validates. */
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  page: number;
  perPage: number;
}

export async function fetchAttendanceRegister(
  countryCode: CountryCode,
  filters: AttendanceFilters,
  signal?: AbortSignal,
): Promise<Paged<AttendanceRow>> {
  return apiRequest<Paged<AttendanceRow>>(`/countries/${countryCode}/field/attendance`, {
    query: {
      user_id: filters.userId,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export interface VisitFilters {
  userId?: string;
  partyId?: string;
  /**
   * PASSED THROUGH UNVALIDATED, deliberately — the testing register's rule.
   * The server owns the vocabulary and answers 400 naming it; a hand-typed
   * `?purpose=bogus` silently dropped would render the WHOLE register under a
   * URL claiming to be filtered.
   */
  purpose?: string;
  dateFrom?: string;
  dateTo?: string;
  open?: boolean;
  page: number;
  perPage: number;
}

export async function fetchVisitRegister(
  countryCode: CountryCode,
  filters: VisitFilters,
  signal?: AbortSignal,
): Promise<Paged<VisitRow>> {
  return apiRequest<Paged<VisitRow>>(`/countries/${countryCode}/field/visits`, {
    query: {
      user_id: filters.userId,
      party_id: filters.partyId,
      purpose: filters.purpose,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      open: filters.open,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export async function fetchTimeline(
  countryCode: CountryCode,
  userId: string,
  date: string,
  signal?: AbortSignal,
): Promise<FieldTimeline> {
  return apiRequest<FieldTimeline>(`/countries/${countryCode}/field/timeline`, {
    query: { user_id: userId, date },
    signal,
  });
}

export async function fetchFieldConfig(
  countryCode: CountryCode,
  signal?: AbortSignal,
): Promise<FieldConfig> {
  return apiRequest<FieldConfig>(`/countries/${countryCode}/field/config`, { signal });
}

export async function updateFieldConfig(
  countryCode: CountryCode,
  patch: FieldConfigPatch,
): Promise<FieldConfig> {
  return apiRequest<FieldConfig>(`/countries/${countryCode}/field/config`, {
    method: 'PATCH',
    body: patch,
  });
}

/* ──────────────────────────────────────────────────────────────────── keys ── */

export const fieldKeys = {
  attendance: (countryCode: CountryCode, filters: AttendanceFilters) =>
    ['field', countryCode, 'attendance', filters] as const,
  visits: (countryCode: CountryCode, filters: VisitFilters) =>
    ['field', countryCode, 'visits', filters] as const,
  timeline: (countryCode: CountryCode, userId: string, date: string) =>
    ['field', countryCode, 'timeline', userId, date] as const,
  config: (countryCode: CountryCode) => ['field', countryCode, 'config'] as const,
};

/* ─────────────────────────────────────────────────────────────────── error ── */

/**
 * One sentence for a failed field read/write. The API's envelope message is a
 * human sentence naming the actual refusal (a 400 names the vocabulary, a 409
 * names the toggle) and is preferred verbatim — the registers' rule.
 */
export function fieldErrorText(error: unknown): string {
  if (error instanceof ApiRequestError && error.message !== '') return error.message;
  if (error instanceof ApiTransportError) return error.message;
  return 'The request failed. Check your connection and try again.';
}

/* ═══════════════════════════════ v22-D · the field map ═══════════════════ */

export interface FieldMapPerson {
  user_id: string;
  name: string;
  lat: string;
  lng: string;
  accuracy_m: string | null;
  captured_at: string;
  source: 'live_session' | 'clock_in' | 'clock_out' | 'visit_arrive' | 'visit_leave';
  live: boolean;
  live_ends_at: string | null;
}

export interface FieldMapPayload {
  country_code: CountryCode;
  tracking_enabled: boolean;
  /** The server's clock — ages are computed against THIS, never the browser's. */
  server_time: string;
  people: FieldMapPerson[];
}

export async function fetchFieldMap(
  countryCode: CountryCode,
  signal?: AbortSignal,
): Promise<FieldMapPayload> {
  return apiRequest<FieldMapPayload>(`/countries/${countryCode}/field/map`, { signal });
}

